import React, { useReducer } from 'react';
import { useIsMobile } from '../../../../hooks';
import { useShouldShowClientNavBar } from '../../../consumer/ClientNavBar/hooks';
import analytics from '../../../../modules/analytics';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import SentimentSurveyContainer from '../../Surveys/SentimentSurvey';
import {
  PROFILE_FEEDBACK_NOTIFICATION_PHOTOS_CHOICE,
  PROFILE_FEEDBACK_NOTIFICATION_REVIEWS_CHOICE,
  PROFILE_FEEDBACK_NOTIFICATION_SERVICES_CHOICE,
} from '../../../provider/notifications/ProfileFeedbackNotification/ProfileFeedbackNotification';
import { sendProfileFeedback } from '../../../../modules/Api';
import { setItem } from '../../../../modules/KeyValueStorage';

const ACTIONS = {
  CHOICE_SELECTED: 'CHOICE_SELECTED',
  RATED_HIGH: 'RATED_HIGH',
  RATED_LOW: 'RATED_LOW',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.CHOICE_SELECTED:
      return {
        ...state,
        canSubmit: true,
      };
    case ACTIONS.RATED_HIGH:
      return {
        ...state,
        primaryInputQuestion: 'Tell them what you think about their profile.',
        canSubmit: true,
      };
    case ACTIONS.RATED_LOW:
      analytics.track('client_profile_feedback_constructive_viewed');
      return {
        ...state,
        useStarRatings: false,
        canSubmit: false,
        primaryInputQuestion: null,
        questionOne: 'What needs work?',
        multiChoiceOptions: [
          'Services didn\'t have enough detail in their descriptions',
          'I want to see more photos of this pro\'s work',
          'I want to see more reviews of this profile',
        ],
      };
    default:
      return state;
  }
};

export const ProfileSentimentSurvey = ({
  profile,
}: {
  profile?: Pick<PublicProviderProfile, 'id' | 'name'>,
}) => {
  const isMobile = useIsMobile();
  const { shouldShowClientNav } = useShouldShowClientNavBar();
  const {
    id,
    name,
  } = profile || {};
  const localForageKey = `profileLaunchSurvey-${id}`;
  const [firstName] = name?.split?.(' ') || [];
  const initialState = {
    questionOne: `${firstName} is new to StyleSeat. What do you think of their profile?`,
    primaryInputQuestion: null,
    placeholder: 'Ex. Very professional, love the photos!',
    useStarRatings: true,
    canSubmit: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleChoiceSelected = choice => {
    switch (choice) {
      case (PROFILE_FEEDBACK_NOTIFICATION_SERVICES_CHOICE):
        analytics.track('client_profile_feedback_constructive_edit_services_click');
        break;
      case (PROFILE_FEEDBACK_NOTIFICATION_PHOTOS_CHOICE):
        analytics.track('client_profile_feedback_constructive_add_photos_click');
        break;
      case (PROFILE_FEEDBACK_NOTIFICATION_REVIEWS_CHOICE):
        analytics.track('client_profile_feedback_constructive_more_reviews_click');
        break;
      default:
        break;
    }
    dispatch({ type: ACTIONS.CHOICE_SELECTED });
  };

  const handleStarSelected = star => {
    if (star > 3) {
      dispatch({
        type: ACTIONS.RATED_HIGH,
      });
    } else {
      dispatch({
        type: ACTIONS.RATED_LOW,
      });
    }
  };

  const handleSurveySubmit = data => {
    const {
      comment,
      ratingStars,
      selectedChoice,
    } = data;
    setItem(localForageKey, true);
    sendProfileFeedback(profile.id, comment, ratingStars, selectedChoice);
    if (ratingStars < 4) {
      analytics.track('client_profile_feedback_constructive_submit_click');
    }
  };

  return (
    <SentimentSurveyContainer
      surveyName="client_new_pro_profile_launch_survey"
      onStarSelected={handleStarSelected}
      onChoiceSelected={handleChoiceSelected}
      onSurveySubmit={handleSurveySubmit}
      trackingParams={{
        providerId: id,
      }}
      adjustForNavBar={isMobile && shouldShowClientNav}
      {...state}
    />
  );
};
